import { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot, getDoc, setDoc, serverTimestamp, Timestamp, collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { SidebarProvider } from "@/components/ui/sidebar"
import WorkspaceSidebar from './WorkspaceSidebar';
import WorkspaceHeader from './WorkspaceHeader';
import { cn } from "@/lib/utils";
import { Lock, ArrowRight, LogOut } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";

type WorkspaceSubscription = {
  status: string;
  plan: string;
  expiry?: Timestamp | Date | number | string;
};

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const parsed = new Date(v);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof v === 'object' && 'toDate' in v) {
    const maybeTimestamp = v as { toDate?: unknown };
    if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate();
  }
  return null;
};

const isSubscriptionExpired = (sub?: WorkspaceSubscription | null): boolean => {
  if (!sub) return true;

  const expiry = toDate(sub.expiry);
  const now = new Date();

  if (sub.status === 'paused') return true;
  if (sub.status === 'active') return expiry !== null && now > expiry;
  if (sub.status === 'trial') return expiry === null || now > expiry;

  return true;
};

// Simple Layout Shell
const WorkspaceLayout = () => {
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [isExpired, setIsExpired] = useState(false); // Default OPEN — checkingExpiry=true spinner guards until Firestore confirms
  const [checkingExpiry, setCheckingExpiry] = useState(true);
  const [subscription, setSubscription] = useState<WorkspaceSubscription | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
     const unsubscribeAuth = auth.onAuthStateChanged((user) => {
         if (user) {
             const userDocRef = doc(db, 'teaching_workspaces', user.uid);
             const unsubSnap = onSnapshot(userDocRef, async (snap) => {
                 if (snap.exists()) {
                     const data = snap.data();
                     const sub = data.subscription as WorkspaceSubscription | undefined;

                     if (sub) {
                         setSubscription(sub);
                         const nextIsExpired = isSubscriptionExpired(sub);
                         const expiry = toDate(sub.expiry);
                         const now = new Date();

                         if (snap.metadata.fromCache && nextIsExpired) {
                             // Avoid flashing a stale cached lock while Firestore confirms the paid state.
                             setCheckingExpiry(true);
                             return;
                         }

                         setIsExpired(nextIsExpired);

                         if (sub.status === 'paused') {
                             // Explicitly paused = blocked
                             setIsExpired(true);
                         } else if (sub.status === 'active') {
                             // Paid subscription — blocked only if expiry is set AND in the past
                             setIsExpired(expiry !== null && now > expiry);
                         } else if (sub.status === 'trial') {
                             // Trial — blocked only if expiry has passed (trialClaimedAt is optional;
                             // bootstrapped trials don't write it but are still valid)
                             setIsExpired(expiry === null || now > expiry);
                         } else {
                             // Unknown status = blocked
                             setIsExpired(true);
                         }
                     } else {
                         // Workspace exists but NO subscription field at all = blocked
                         if (snap.metadata.fromCache) {
                             setCheckingExpiry(true);
                             return;
                         }
                         setIsExpired(true);
                     }
                 } else {
                     if (snap.metadata.fromCache) {
                         setCheckingExpiry(true);
                         return;
                     }

                     // Bootstrap the trial if the workspace config doesn't exist at all
                     let trialDays = 14; 
                     const configRef = doc(db, "system_config", "independent_settings");
                     try {
                         const configSnap = await getDoc(configRef);
                         if (configSnap.exists() && configSnap.data().trialDays) {
                             trialDays = configSnap.data().trialDays;
                         }
                         const now = new Date();
                          const expiryDate = new Date(now.getTime() + trialDays * 24 * 60 * 60 * 1000);
                          
                          const name = user.displayName || "Teacher";
                          await setDoc(userDocRef, {
                              ownerId: user.uid,
                              displayName: `${name}'s Workspace`,
                              email: user.email,
                              createdAt: serverTimestamp(),
                              subscription: {
                                  status: 'trial',
                                  plan: 'individual_trial',
                                  expiry: Timestamp.fromDate(expiryDate)
                              }
                          });
                          const formatted = expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
                          toast({
                              title: 'Free Trial Claimed Successfully',
                              description: `Your free trial has been started and will expire on ${formatted}.`,
                              variant: 'default',
                              duration: 10000,
                          });
                     } catch (error) {
                         console.error("Trial setup error:", error);
                     }
                 }
                 setCheckingExpiry(false);
             });
             return () => unsubSnap();
         } else {
             setCheckingExpiry(false);
         }
     });
     return () => unsubscribeAuth();
  }, [auth]);

  // Fetch payment history so the header can derive an effective plan even
  // when the subscription doc still has a stale trial plan (cloud function
  // not yet deployed, or IPN hasn't fired).
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const fetchTxs = async () => {
      try {
        const txCol = collection(db, `teaching_workspaces/${uid}/transactions`);
        const snap = await getDocs(query(txCol, orderBy('completedAt', 'desc')));
        setTransactions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch { /* non-critical */ }
    };
    fetchTxs();
  }, [auth.currentUser?.uid]);

  // Single Source of Truth: We strictly rely on the Firestore document's `plan` field.
  // We removed the transaction-history override because it caused race conditions
  // where users would be temporarily downgraded or upgraded if their payment history
  // conflicted with the admin/webhook's final decision in Firestore.
  const effectivePlan = subscription?.plan || '';

  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const handleSignOut = () => {
    setShowSignOutConfirm(true);
  };

  const confirmSignOut = async () => {
    setShowSignOutConfirm(false);
    localStorage.removeItem('cachedAuth');
    localStorage.removeItem('teach_auth_uid'); // Clear auth hint so fast-path guard resets
    sessionStorage.removeItem('quickAuth');
    await auth.signOut();
    navigate('/login');
  };

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_expanded');
    return saved === null ? true : saved === 'true';
  });
  const [isDocumentFocusMode, setIsDocumentFocusMode] = useState(false);
  const isSearchUtility = location.pathname.includes('/dashboard/utilities/search');
  const isPdfCreateUtility = location.pathname.includes('/dashboard/utilities/pdf-converter/create');
  const isFullBleedUtility = isSearchUtility || isPdfCreateUtility || isDocumentFocusMode;

  const handleSidebarChange = (open: boolean) => {
    setIsSidebarOpen(open);
    localStorage.setItem('sidebar_expanded', String(open));
  };

  useEffect(() => {
    const handleWorkspaceSidebar = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean; documentFocus?: boolean }>).detail;
      if (typeof detail?.open === 'boolean') {
        setIsSidebarOpen(detail.open);
      }
      if (typeof detail?.documentFocus === 'boolean') {
        setIsDocumentFocusMode(detail.documentFocus);
      }
    };

    window.addEventListener('sonoma:workspace-sidebar', handleWorkspaceSidebar);
    return () => window.removeEventListener('sonoma:workspace-sidebar', handleWorkspaceSidebar);
  }, []);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={handleSidebarChange}>
      <div className="flex h-screen w-full bg-white dark:bg-[#020617] font-sans selection:bg-black selection:text-white overflow-hidden">
        {!isDocumentFocusMode && (
          <WorkspaceSidebar onSignOut={handleSignOut} pathname={location.pathname} plan={subscription?.plan || ''} />
        )}
        <main className="flex-1 flex flex-col min-w-0 bg-white dark:bg-[#020617] transition-all duration-300 ease-in-out relative overflow-hidden">
          {!isDocumentFocusMode && (
            <WorkspaceHeader pathname={location.pathname} user={auth.currentUser} subscription={subscription} effectivePlan={effectivePlan} />
          )}
          <div ref={scrollContainerRef} className={cn(
            "flex-1 flex flex-col min-h-0 relative",
            isFullBleedUtility ? "overflow-hidden p-0" : "overflow-y-auto overflow-x-hidden scroll-smooth",
            !isFullBleedUtility && !location.pathname.includes('/dashboard/lessons/course/') ? "p-2 sm:p-4 md:p-10" : "p-0"
          )}>
          <div className={cn(
              "w-full flex-1 flex flex-col relative",
              isFullBleedUtility ? "h-full" : "h-auto"
            )}>
              {checkingExpiry ? (
                /* Hard gate: nothing renders until Firestore confirms subscription status */
                <div className="flex-1 flex items-center justify-center min-h-[400px]">
                  <div className="flex flex-col items-center gap-3 opacity-30">
                    <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-400">Verifying access...</span>
                  </div>
                </div>
              ) : isExpired && !location.pathname.includes('/dashboard/billing') && !location.pathname.includes('/dashboard/account') ? (
                  <div className="flex-1 min-h-[400px] flex items-center justify-center relative p-6">
                      {/* Technical Red Signal Line - Top Only */}
                      <div className="absolute inset-x-20 top-0 h-px bg-gradient-to-r from-transparent via-red-500/40 to-transparent"></div>
                      
                      <div className="text-center space-y-6 max-w-sm p-8 z-10 bg-white dark:bg-transparent rounded-none border border-slate-300 dark:border-white/10 transition-all duration-300">
                          <div className="w-12 h-12 bg-red-500/5 dark:bg-red-500/10 rounded-none flex items-center justify-center mx-auto border border-red-500/10 dark:border-red-500/20">
                              <Lock className="w-5 h-5 text-red-600/80 dark:text-red-500/80" />
                          </div>
                          
                          <div className="space-y-2">
                              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-widest uppercase italic">Platform Access Locked</h2>
                              <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed max-w-[240px] mx-auto opacity-70">
                                  Your free trial or subscription has expired. Please renew to continue using the platform.
                              </p>
                          </div>
                          
                          <button 
                              onClick={() => navigate('/dashboard/billing')}
                              className="w-full h-10 flex items-center justify-center gap-2 bg-red-600 text-white font-black uppercase tracking-[0.3em] text-[9px] transition-all hover:bg-red-700 active:scale-[0.98] rounded-none shadow-xl shadow-red-600/10"
                          >
                              Check Billing & Plans <ArrowRight className="w-3 h-3" />
                          </button>
                      </div>
                  </div>
              ) : (location.pathname.includes('/dashboard/students') || location.pathname.includes('/dashboard/classes')) && !(subscription?.plan || '').toLowerCase().includes('tier2') ? (
                  <div className="flex-1 min-h-[400px] flex items-center justify-center relative p-6">
                      <div className="absolute inset-x-20 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/40 to-transparent"></div>
                      
                      <div className="text-center space-y-6 max-w-sm p-8 z-10 bg-white dark:bg-[#0A0A0A] rounded-xl border border-slate-200 dark:border-white/10 shadow-lg">
                          <div className="w-14 h-14 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto">
                              <Lock className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          
                          <div className="space-y-3">
                              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">Premium Feature</h2>
                              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed max-w-[240px] mx-auto">
                                  Student and Class management are exclusive to Tier 2 subscriptions. Upgrade your plan to unlock this workflow.
                              </p>
                          </div>
                          
                          <button 
                              onClick={() => navigate('/dashboard/billing')}
                              className="w-full h-11 flex items-center justify-center gap-2 bg-indigo-600 text-white font-semibold text-sm transition-all hover:bg-indigo-700 active:scale-95 rounded-lg shadow-md"
                          >
                              Upgrade to Tier 2 <ArrowRight className="w-4 h-4" />
                          </button>
                      </div>
                  </div>
              ) : (
                <Outlet />
              )}
            </div>
          </div>
        </main>
      </div>

      {showSignOutConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/20 dark:bg-slate-950/60 backdrop-blur-[2px] animate-in fade-in duration-200">
          <div className="text-center space-y-6 max-w-sm w-full p-8 bg-white dark:bg-[#020617] rounded-none border border-slate-200 dark:border-white/10 shadow-2xl animate-in zoom-in-95 duration-200 mx-4">
            <div className="w-12 h-12 bg-red-500/5 dark:bg-red-500/10 rounded-none flex items-center justify-center mx-auto border border-red-500/10 dark:border-red-500/20">
              <LogOut className="w-5 h-5 text-red-600/80 dark:text-red-500/80" />
            </div>

            <div className="space-y-2">
              <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-widest uppercase italic">Sign Out</h2>
              <p className="text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed max-w-[240px] mx-auto opacity-70">
                Are you sure you want to end your workspace session?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSignOutConfirm(false)}
                className="flex-1 h-10 flex items-center justify-center bg-transparent border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 font-black uppercase tracking-[0.3em] text-[9px] transition-all hover:bg-slate-50 dark:hover:bg-white/5 active:scale-[0.98]"
              >
                Cancel
              </button>
              <button
                onClick={confirmSignOut}
                className="flex-1 h-10 flex items-center justify-center bg-red-600 text-white font-black uppercase tracking-[0.3em] text-[9px] transition-all hover:bg-red-700 active:scale-[0.98] shadow-xl shadow-red-600/10"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </SidebarProvider>
  );
};

export default WorkspaceLayout;
